/** Proactive flirty nudges — continue through the session (even after the fan replies). */

export type ProactiveNudgeKind = 'opening' | 'idle';

export const CREATOR_CHAT_PROACTIVE = {
  /** After open — sooner if there is no static welcome message */
  openingDelayMs: 7000,
  openingDelayWithWelcomeMs: 14000,
  /** Repeat for the whole session until maxNudgesPerSession */
  idleRepeatMs: 22000,
  /** While LLM or TTS is busy, retry sooner so we don't wait a full idle cycle */
  busyRetryMs: 4000,
  maxNudgesPerSession: 15,
} as const;

type ScheduleOpts = {
  enabled: boolean;
  active: boolean;
  hasWelcome: boolean;
  isBusy: () => boolean;
  onNudge: (kind: ProactiveNudgeKind) => void | Promise<void>;
};

/** Schedule opening + idle nudges. Returns cleanup (clear timers). */
export function startCreatorChatProactiveSchedule(opts: ScheduleOpts): () => void {
  if (!opts.enabled || !opts.active) {
    return () => undefined;
  }

  const timers: number[] = [];
  let nudgeCount = 0;
  let openingScheduled = false;
  let idleLoopStarted = false;

  const clearAll = () => {
    timers.forEach((t) => window.clearTimeout(t));
    timers.length = 0;
  };

  const canNudge = () =>
    opts.active && !opts.isBusy() && nudgeCount < CREATOR_CHAT_PROACTIVE.maxNudgesPerSession;

  const runNudge = async (kind: ProactiveNudgeKind) => {
    if (!canNudge()) return;
    nudgeCount += 1;
    await opts.onNudge(kind);
  };

  const scheduleIdleLoop = () => {
    if (idleLoopStarted) return;
    idleLoopStarted = true;

    const tick = () => {
      if (!opts.active) {
        clearAll();
        return;
      }
      if (!canNudge()) {
        if (nudgeCount >= CREATOR_CHAT_PROACTIVE.maxNudgesPerSession) {
          return;
        }
        const retry = window.setTimeout(tick, CREATOR_CHAT_PROACTIVE.busyRetryMs);
        timers.push(retry);
        return;
      }
      void runNudge('idle').finally(() => {
        if (!opts.active) {
          clearAll();
          return;
        }
        if (nudgeCount < CREATOR_CHAT_PROACTIVE.maxNudgesPerSession) {
          const next = window.setTimeout(tick, CREATOR_CHAT_PROACTIVE.idleRepeatMs);
          timers.push(next);
        }
      });
    };

    const firstIdle = window.setTimeout(tick, CREATOR_CHAT_PROACTIVE.idleRepeatMs);
    timers.push(firstIdle);
  };

  const openingDelay = opts.hasWelcome
    ? CREATOR_CHAT_PROACTIVE.openingDelayWithWelcomeMs
    : CREATOR_CHAT_PROACTIVE.openingDelayMs;

  if (!openingScheduled) {
    openingScheduled = true;
    const openingTimer = window.setTimeout(() => {
      if (!canNudge()) {
        scheduleIdleLoop();
        return;
      }
      void runNudge('opening').finally(() => {
        scheduleIdleLoop();
      });
    }, openingDelay);
    timers.push(openingTimer);
  }

  return clearAll;
}
